import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { Add, Close, DeleteOutline } from "@mui/icons-material";
import { Helmet } from "react-helmet";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Axios from "../axios";

const TRAINING_ESSENTIAL_TYPES = ["Nutrition", "Hydration", "Meal Prep", "Recovery", "Apparel"];

const MAX_IMAGES = 5;

const blankItem = {
  type: "",
  name: "",
  brand: "",
  description: "",
  price: "",
  taxRate: "",
  tags: [],
  images: [],
  points: [""],
  otherComments: "",
};

const TrainingEssentialsForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const BACKEND_URL = process.env.REACT_APP_BACKEND;
  const [item, setItem] = useState(blankItem);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [tagSuggestions, setTagSuggestions] = useState([]);
  const [tagInput, setTagInput] = useState("");

  const showError = (err) => {
    toast.error(err?.response?.data?.message || err?.response?.data?.error || "Something went wrong");
  };

  useEffect(() => {
    Axios.get(`${BACKEND_URL}training-essentials/tags/suggestions`)
      .then((res) => setTagSuggestions(Array.isArray(res?.data?.data) ? res.data.data : []))
      .catch(() => {});
  }, [BACKEND_URL]);

  useEffect(() => {
    const urls = item.images.map((image) =>
      image instanceof File ? URL.createObjectURL(image) : image
    );
    setImagePreviews(urls);
    return () => {
      urls.forEach((url, index) => {
        if (item.images[index] instanceof File) URL.revokeObjectURL(url);
      });
    };
  }, [item.images]);

  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    Axios.get(`${BACKEND_URL}training-essentials/${id}`)
      .then((res) => {
        const data = res?.data?.data || {};
        setItem({
          type: data.type || "",
          name: data.name || "",
          brand: data.brand || "",
          description: data.description || "",
          price: data.price ?? "",
          taxRate: data.taxRate ?? "",
          tags: Array.isArray(data.tags) ? data.tags : [],
          images: Array.isArray(data.images) ? data.images : [],
          points: Array.isArray(data.points) && data.points.length ? data.points : [""],
          otherComments: data.otherComments || "",
        });
      })
      .catch(showError)
      .finally(() => setLoading(false));
  }, [BACKEND_URL, id, isEdit]);

  const updateItem = (key, value) => {
    setItem((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const persistNewTag = (name) => {
    if (tagSuggestions.some((suggestion) => suggestion.toLowerCase() === name.toLowerCase())) return;
    Axios.post(`${BACKEND_URL}training-essentials/tags/suggestions`, { name })
      .then((res) => {
        const savedName = res?.data?.data || name;
        setTagSuggestions((prev) =>
          prev.some((suggestion) => suggestion.toLowerCase() === savedName.toLowerCase())
            ? prev
            : [...prev, savedName].sort((a, b) => a.localeCompare(b))
        );
      })
      .catch(() => {});
  };

  const addTag = (rawValue) => {
    const value = rawValue.trim();
    if (!value) return;
    setItem((prev) =>
      prev.tags.some((tag) => tag.toLowerCase() === value.toLowerCase())
        ? prev
        : { ...prev, tags: [...prev.tags, value] }
    );
    persistNewTag(value);
  };

  const updatePoint = (index, value) => {
    setItem((prev) => ({
      ...prev,
      points: prev.points.map((point, pointIndex) => (pointIndex === index ? value : point)),
    }));
    setErrors((prev) => ({ ...prev, [`points_${index}`]: "" }));
  };

  const addPoint = () => {
    setItem((prev) => ({ ...prev, points: [...prev.points, ""] }));
  };

  const removePoint = (index) => {
    setItem((prev) => ({
      ...prev,
      points: prev.points.filter((_, pointIndex) => pointIndex !== index),
    }));
  };

  const addImages = (files) => {
    const selected = Array.from(files || []);
    if (!selected.length) return;
    setItem((prev) => ({
      ...prev,
      images: [...prev.images, ...selected].slice(0, MAX_IMAGES),
    }));
    setErrors((prev) => ({ ...prev, images: "" }));
  };

  const removeImage = (index) => {
    setItem((prev) => ({
      ...prev,
      images: prev.images.filter((_, imageIndex) => imageIndex !== index),
    }));
  };

  const validate = () => {
    const nextErrors = {};

    if (!item.name.trim()) nextErrors.name = "Name is required";
    if (!item.brand.trim()) nextErrors.brand = "Brand is required";
    if (!TRAINING_ESSENTIAL_TYPES.includes(item.type)) nextErrors.type = "Type is required";
    if (!item.description.trim()) nextErrors.description = "Description is required";

    if (!String(item.price).trim()) {
      nextErrors.price = "Price is required";
    } else if (Number.isNaN(Number(item.price)) || Number(item.price) < 0) {
      nextErrors.price = "Price must be 0 or greater";
    }

    if (String(item.taxRate).trim()) {
      const taxRateValue = Number(item.taxRate);
      if (Number.isNaN(taxRateValue) || taxRateValue < 0 || taxRateValue > 100) {
        nextErrors.taxRate = "Tax rate must be between 0 and 100";
      }
    }

    if (!item.images.length) {
      nextErrors.images = "At least one image is required";
    } else {
      item.images.forEach((image) => {
        if (image instanceof File) {
          if (!image.type.startsWith("image/")) nextErrors.images = "Images must be valid image files";
          else if (image.size > 2 * 1024 * 1024) nextErrors.images = "Each image must be under 2MB";
        }
      });
    }

    (item.points || []).forEach((point, index) => {
      if (!point.trim()) nextErrors[`points_${index}`] = "This field is required";
    });

    return nextErrors;
  };

  const submit = () => {
    const nextErrors = validate();
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      toast.error("Please fill out the mandatory fields");
      return;
    }

    setLoading(true);
    const payload = new FormData();
    payload.append("type", item.type);
    payload.append("name", item.name.trim());
    payload.append("brand", item.brand.trim());
    payload.append("description", item.description.trim());
    payload.append("price", item.price === "" ? 0 : item.price);
    payload.append("taxRate", item.taxRate === "" ? 0 : item.taxRate);
    payload.append("tags", JSON.stringify(item.tags || []));
    payload.append("otherComments", item.otherComments.trim());
    payload.append(
      "points",
      JSON.stringify((item.points || []).map((point) => point.trim()).filter(Boolean))
    );

    const existingImages = item.images.filter((image) => typeof image === "string");
    payload.append("existingImages", JSON.stringify(existingImages));
    item.images
      .filter((image) => image instanceof File)
      .forEach((file) => payload.append("images", file));

    const request = isEdit
      ? Axios.put(`${BACKEND_URL}training-essentials/${id}`, payload, { headers: { "Content-Type": "multipart/form-data" } })
      : Axios.post(`${BACKEND_URL}training-essentials`, payload, { headers: { "Content-Type": "multipart/form-data" } });

    request
      .then((res) => {
        toast.success(res?.data?.message || "Saved successfully");
        navigate("/training-essentials");
      })
      .catch(showError)
      .finally(() => setLoading(false));
  };

  return (
    <div className="container-fluid">
      <Helmet>
        <title>{isEdit ? "Edit Training Essential" : "Create Training Essential"}</title>
      </Helmet>
      <div className="d-flex justify-content-between align-center mb-3">
        <h2>{isEdit ? "Edit Training Essential" : "Create Training Essential"}</h2>
        <Button onClick={() => navigate("/training-essentials")}>Back</Button>
      </div>

      <Typography variant="h6" sx={{ mb: 2 }}>General Details</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            required
            label="Name"
            value={item.name}
            onChange={(e) => updateItem("name", e.target.value)}
            error={Boolean(errors.name)}
            helperText={errors.name}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            required
            label="Brand"
            placeholder="e.g. Maurten, GU, Clif, Skratch Labs"
            value={item.brand}
            onChange={(e) => updateItem("brand", e.target.value)}
            error={Boolean(errors.brand)}
            helperText={errors.brand}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            required
            select
            label="Type"
            value={item.type}
            onChange={(e) => updateItem("type", e.target.value)}
            error={Boolean(errors.type)}
            helperText={errors.type}
          >
            {TRAINING_ESSENTIAL_TYPES.map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            required
            multiline
            minRows={3}
            label="Description"
            value={item.description}
            onChange={(e) => updateItem("description", e.target.value)}
            error={Boolean(errors.description)}
            helperText={errors.description}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            required
            type="number"
            label="Price"
            value={item.price}
            onChange={(e) => updateItem("price", e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            error={Boolean(errors.price)}
            helperText={errors.price}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            type="number"
            label="Tax Rate"
            placeholder="0"
            value={item.taxRate}
            onChange={(e) => updateItem("taxRate", e.target.value)}
            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
            error={Boolean(errors.taxRate)}
            helperText={errors.taxRate || "Sales tax applied to this item at checkout. Leave blank for no tax."}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Autocomplete
            multiple
            freeSolo
            options={tagSuggestions}
            value={item.tags}
            inputValue={tagInput}
            onChange={(_, newValue) => {
              const cleaned = [];
              newValue.forEach((entry) => {
                String(entry)
                  .split(",")
                  .map((part) => part.trim())
                  .filter(Boolean)
                  .forEach((part) => {
                    if (!cleaned.some((tag) => tag.toLowerCase() === part.toLowerCase())) cleaned.push(part);
                  });
              });
              const newlyAdded = cleaned.filter(
                (tag) => !item.tags.some((existing) => existing.toLowerCase() === tag.toLowerCase())
              );
              setItem((prev) => ({ ...prev, tags: cleaned }));
              newlyAdded.forEach(persistNewTag);
              setTagInput("");
            }}
            onInputChange={(_, newInputValue, reason) => {
              if (reason === "input" && newInputValue.includes(",")) {
                const parts = newInputValue.split(",");
                parts.slice(0, -1).forEach((part) => addTag(part));
                setTagInput(parts[parts.length - 1]);
              } else {
                setTagInput(newInputValue);
              }
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip variant="outlined" label={option} {...getTagProps({ index })} key={option} />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Tags"
                placeholder="Select or type a tag, then press , to add"
                error={Boolean(errors.tags)}
                helperText={errors.tags}
              />
            )}
          />
        </Grid>
      </Grid>

      <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>Images</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Upload up to {MAX_IMAGES} images. Customers will be able to swipe through them on the storefront.
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              {imagePreviews.length > 0 ? (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 2 }}>
                  {imagePreviews.map((preview, index) => (
                    <Box
                      key={index}
                      sx={{
                        position: "relative",
                        width: 140,
                        height: 140,
                        borderRadius: 1,
                        overflow: "hidden",
                        border: "1px solid #ddd",
                      }}
                    >
                      <img
                        src={preview}
                        alt={`Image ${index + 1}`}
                        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", backgroundColor: "#f7f7f7" }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => removeImage(index)}
                        sx={{
                          position: "absolute",
                          top: 4,
                          right: 4,
                          bgcolor: "rgba(255,255,255,0.85)",
                          "&:hover": { bgcolor: "rgba(255,255,255,0.95)" },
                        }}
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: "italic" }}>
                  No images selected.
                </Typography>
              )}
              <TextField
                type="file"
                fullWidth
                inputProps={{ accept: "image/*", multiple: true }}
                onChange={(e) => {
                  addImages(e.target.files);
                  e.target.value = "";
                }}
                disabled={item.images.length >= MAX_IMAGES}
                error={Boolean(errors.images)}
                helperText={
                  errors.images ||
                  (item.images.length >= MAX_IMAGES
                    ? `Maximum of ${MAX_IMAGES} images reached`
                    : `Add image (max 2MB each, ${item.images.length}/${MAX_IMAGES} used)`)
                }
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>Points</Typography>
      {(item.points || []).map((point, index) => (
        <Box key={`point-${index}`} sx={{ display: "flex", gap: 1, mb: 1 }}>
          <TextField
            fullWidth
            required
            label={`Point ${index + 1}`}
            placeholder="e.g. Personalized nutrition plan"
            value={point}
            onChange={(e) => updatePoint(index, e.target.value)}
            error={Boolean(errors[`points_${index}`])}
            helperText={errors[`points_${index}`]}
          />
          <IconButton onClick={() => removePoint(index)} disabled={item.points.length === 1}>
            <DeleteOutline />
          </IconButton>
        </Box>
      ))}
      <Button startIcon={<Add />} sx={{ mt: 1 }} onClick={addPoint}>
        Add Point
      </Button>

      <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>Other Comments</Typography>
      <TextField
        fullWidth
        multiline
        minRows={3}
        label="Other Comments"
        value={item.otherComments}
        onChange={(e) => updateItem("otherComments", e.target.value)}
      />

      <Box sx={{ mt: 4, mb: 4, display: "flex", gap: 2 }}>
        <Button variant="contained" disabled={loading} onClick={submit}>
          {loading ? "Saving..." : isEdit ? "Update" : "Submit"}
        </Button>
        <Button onClick={() => navigate("/training-essentials")}>Cancel</Button>
      </Box>
    </div>
  );
};

export default TrainingEssentialsForm;